// src/views/scannedData/SelectedDataDisplay.js
import PropTypes from "prop-types";

// Map abbreviated codes stored in RTDB back to display labels
const CONF_LABEL = { H: "High", M: "Medium", L: "Low" };
const CONF_STYLE = {
  High: { color: "#166534", bg: "#dcfce7", emoji: "🟢" },
  Medium: { color: "#92400e", bg: "#fef3c7", emoji: "🟡" },
  Low: { color: "#991b1b", bg: "#fee2e2", emoji: "🔴" },
};

export default function SelectedDataDisplay({ selectedData }) {
  if (!selectedData) return null;

  const address = selectedData.editedAddress || selectedData.googleMapAddress || "";

  // trustScore: { dist, conf, isFar? }
  const ts = selectedData.trustScore;
  const confLabel = ts?.conf ? (CONF_LABEL[ts.conf] ?? ts.conf) : null;

  // Determine style: if isFar is true (High Distance), force Low/Red style (even if conf is High)
  // otherwise use the confidence level's style
  let confStyle = confLabel ? (CONF_STYLE[confLabel] ?? CONF_STYLE.Low) : null;
  if (ts?.isFar && confStyle) {
    confStyle = CONF_STYLE.Low; // Force Red for High Distance rejects
  }

  // coords.acc  = scan GPS accuracy
  // photoCoords.acc = photo GPS accuracy
  const scanAcc = selectedData.coordinates?.acc;
  const photoAcc = selectedData.photoCoords?.acc;
  // isFlagged: conf is not High, OR distance tier was "high" (ts.isFar = true covers High+Good+reject)
  const isFlagged = (confLabel && confLabel !== "High") || ts?.isFar === true;

  return (
    <div id="selected-data-box">
      <h3>Selected Data</h3>

      <p><strong>QR Data:</strong> {selectedData.qrcodeData}</p>

      <p>
        <strong>Address:</strong>{" "}
        {address ? (
          <a
            href={`https://www.google.com/maps?q=${encodeURIComponent(address)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="map-link"
          >
            {address}
          </a>
        ) : "N/A"}
      </p>

      {selectedData.coordinates && (
        <p>
          <strong>Coordinates:</strong>{" "}
          {selectedData.coordinates.x}, {selectedData.coordinates.y}
        </p>
      )}

      {/* ---- Location Trust Score (visible to admin) ---- */}
      {ts && (
        <div style={{
          marginTop: "10px",
          padding: "10px 14px",
          borderRadius: "8px",
          backgroundColor: "#f9fafb",
          border: "1px solid #e5e7eb",
          fontSize: "0.85rem",
          lineHeight: "1.8",
        }}>
          <strong style={{ display: "block", marginBottom: "4px" }}>📍 Trust Score</strong>

          {ts.dist != null && <div>📏 <strong>Distance:</strong> {ts.dist} m</div>}
          {scanAcc != null && <div>📡 <strong>Scan GPS:</strong> ±{Math.round(scanAcc)} m</div>}
          {photoAcc != null && <div>📸 <strong>Photo GPS:</strong> ±{Math.round(photoAcc)} m</div>}

          {confStyle && (
            <div style={{ marginTop: "6px", display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" }}>
              <span style={{
                padding: "2px 10px",
                borderRadius: "12px",
                backgroundColor: confStyle.bg,
                color: confStyle.color,
                fontWeight: "bold",
                fontSize: "0.82rem",
              }}>
                {confStyle.emoji} {confLabel} Confidence
              </span>
              {isFlagged && (
                <span style={{
                  padding: "2px 10px",
                  borderRadius: "12px",
                  backgroundColor: "#fef3c7",
                  color: "#92400e",
                  fontWeight: "bold",
                  fontSize: "0.82rem",
                }}>
                  ⚑ Flagged
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {selectedData.urlToReadImage && (
        <p>
          <strong>Image:</strong>{" "}
          <a href={selectedData.urlToReadImage} target="_blank" rel="noopener noreferrer">View</a>
        </p>
      )}

      {selectedData.note && (
        <p><strong>Note:</strong> {selectedData.note}</p>
      )}
    </div>
  );
}

SelectedDataDisplay.propTypes = {
  selectedData: PropTypes.object,
};
